import React, { useEffect, useRef, useState } from 'react';
import ModalCloseButton from '../Modal/ModalCloseButton';
import ZoomableImage from '../Modal/ZoomableImage';

import './ImageGallery.css';
import { onMount } from '../../hooks/onMount';

export function ImageGallery({ images = [], onCloseRequest }) {
  const galleryRef = useRef(null);
  const zoomedImageCountRef = useRef(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [closeButtonShowRequest, setCloseButtonShowRequest] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  onMount(() => {
    galleryRef?.current?.focus();
  });

  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) {
      return undefined;
    }

    const imageIndexes = new Map();
    gallery.querySelectorAll('.image').forEach((image, index) => {
      imageIndexes.set(image, index);
    });

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio >= 1) {
            const index = imageIndexes.get(entry.target);
            if (index !== undefined) {
              setCurrentImageIndex(index);
            }
          }
        });
      },
      { root: gallery, threshold: 1 }
    );

    imageIndexes.forEach((_, image) => {
      observer.observe(image);
    });

    return () => {
      observer.disconnect();
    };
  }, [images]);

  function handleImageZoomChange(imageIsZoomed) {
    if (imageIsZoomed) {
      zoomedImageCountRef.current += 1;
    } else {
      // Any image zooming out shows the button, even if other images remain zoomed.
      zoomedImageCountRef.current = Math.max(
        0,
        zoomedImageCountRef.current - 1
      );
      setCloseButtonShowRequest(request => request + 1);
    }
    setIsZoomed(zoomedImageCountRef.current > 0);
  }

  return (
    <div
      className="ImageGallery"
      tabIndex={0}
      ref={galleryRef}
      onClick={event => {
        if (event.target === event.currentTarget) {
          onCloseRequest();
        }
      }}
    >
      <ModalCloseButton
        onCloseRequest={onCloseRequest}
        shouldHide={isZoomed}
        showRequest={closeButtonShowRequest}
      />
      {images.map((image, index) => (
        <ZoomableImage
          loading={
            index === 0 || index === currentImageIndex + 1 ? 'eager' : 'lazy'
          }
          key={image.url}
          src={image.url}
          alt="Gallery image"
          width={image.w}
          height={image.h}
          className="image"
          allowNativePan
          onZoomChange={handleImageZoomChange}
        />
      ))}
    </div>
  );
}
